import React, { useMemo, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import styles from "./network.module.css";
import { Button, Section, SectionIntro } from "../../../../COMPONENTS/UI";
import {
  cityCoordinates,
  headquarters,
  stateLocations,
} from "../../../../CONTENT/locations";
import { investigators } from "../../../../CONSTANTS/investigatorsList";

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();
const sameCity = (a, b) => {
  const x = norm(a);
  const y = norm(b);
  return x === y || x.startsWith(y) || y.startsWith(x);
};
const asList = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const buildNetwork = () => {
  const byState = {};
  Object.entries(stateLocations).forEach(([state, cities]) => {
    byState[state] = { state, cities: [...cities], people: [] };
  });
  investigators.forEach((inv) => {
    const state = inv.location?.state;
    if (!state) return;
    byState[state] ||= { state, cities: [], people: [] };
    byState[state].people.push(inv);
    const city = inv.location.city;
    if (city && !byState[state].cities.some((c) => sameCity(c, city))) {
      byState[state].cities.push(city);
    }
  });
  return Object.values(byState).map((s) => {
    const specialties = [
      ...new Set(s.people.flatMap((p) => asList(p.specialization))),
    ].sort();
    return { ...s, specialties };
  });
};

const Network = () => {
  const states = useMemo(buildNetwork, []);
  const [activeState, setActiveState] = useState(states[0].state);
  const [openCity, setOpenCity] = useState(null);
  const active = states.find((s) => s.state === activeState);
  const kurStates = useMemo(() => new Set(states.map((s) => s.state)), [states]);

  const pick = (name) => {
    setActiveState(name);
    setOpenCity(null);
  };

  const peopleAt = (city) =>
    active.people.filter((p) => sameCity(p.location.city, city));

  return (
    <Section tone="cloud" id="network" labelledBy="network-title">
      <SectionIntro
        id="network-title"
        eyebrow="The KUR network"
        title="Community-based sites, connected as one."
        lead="Explore where KUR investigators practice and the specialties they bring to each community."
      />

      <div className={styles.layout}>
        <div className={styles.mapWrap}>
          <ComposableMap
            projection="geoAlbersUsa"
            className={styles.map}
            role="img"
            aria-label="Map of the United States highlighting states with KUR research sites"
          >
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const name = geo.properties.name;
                  const isKur = kurStates.has(name);
                  const isActive = name === activeState;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onClick={isKur ? () => pick(name) : undefined}
                      style={{
                        default: {
                          fill: isActive
                            ? "var(--kur-amber)"
                            : isKur
                            ? "var(--kur-gold)"
                            : "#dde3e4",
                          stroke: "#fff",
                          strokeWidth: 0.6,
                          outline: "none",
                          cursor: isKur ? "pointer" : "default",
                        },
                        hover: {
                          fill: isKur ? "var(--kur-amber)" : "#dde3e4",
                          stroke: "#fff",
                          outline: "none",
                        },
                        pressed: { outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
            {Object.entries(cityCoordinates).map(([city, coords]) => (
              <Marker key={city} coordinates={coords}>
                <circle
                  r={city === headquarters ? 6 : 4}
                  fill="var(--color-ink)"
                  stroke="#fff"
                  strokeWidth={1.5}
                />
              </Marker>
            ))}
          </ComposableMap>
          <p className={styles.legend}>
            <span className={styles.dot} aria-hidden="true" /> KUR site
            <span className={styles.hq}>Headquarters: {headquarters}, Texas</span>
          </p>
        </div>

        <div className={styles.panel}>
          <div className={styles.states} role="tablist" aria-label="States">
            {states.map((s) => (
              <button
                key={s.state}
                type="button"
                role="tab"
                aria-selected={s.state === activeState}
                aria-controls="network-panel"
                className={styles.stateTab}
                onClick={() => pick(s.state)}
              >
                {s.state}
              </button>
            ))}
          </div>

          <div id="network-panel" role="tabpanel" className={styles.detail}>
            <h3 className={styles.stateName}>{active.state}</h3>
            <dl className={styles.facts}>
              <div>
                <dt>Cities</dt>
                <dd>{active.cities.length}</dd>
              </div>
              <div>
                <dt>Investigators</dt>
                <dd>{active.people.length}</dd>
              </div>
              <div>
                <dt>Specialties</dt>
                <dd>{active.specialties.length}</dd>
              </div>
            </dl>

            {active.specialties.length > 0 && (
              <ul className={styles.chips} aria-label="Specialties">
                {active.specialties.slice(0, 14).map((s) => (
                  <li key={s}>{s}</li>
                ))}
                {active.specialties.length > 14 && (
                  <li>+{active.specialties.length - 14} more</li>
                )}
              </ul>
            )}

            <ul className={styles.cities}>
              {active.cities.map((city) => {
                const people = peopleAt(city);
                const open = openCity === city;
                return (
                  <li key={city} className={styles.city}>
                    <button
                      type="button"
                      className={styles.cityBtn}
                      aria-expanded={open}
                      aria-controls={`city-${city}`}
                      onClick={() => setOpenCity(open ? null : city)}
                    >
                      <span>
                        {city}
                        {city === headquarters && (
                          <em className={styles.hqTag}>HQ</em>
                        )}
                      </span>
                      <span className={styles.count}>
                        {people.length > 0
                          ? `${people.length} investigator${people.length > 1 ? "s" : ""}`
                          : "KUR site"}
                      </span>
                    </button>
                    {open && (
                      <ul id={`city-${city}`} className={styles.people}>
                        {people.length === 0 && (
                          <li>KUR clinical research site</li>
                        )}
                        {people.map((p) => (
                          <li key={p.name}>
                            <strong>{p.name}</strong>
                            <span>{asList(p.specialization).join(", ")}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
            <Button href="/network/locations" variant="link" tone="light">
              View all locations
            </Button>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Network;
